import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { StatPill } from '../ui/StatPill';
import { ServiceDiagnosis } from './ServiceDiagnosis';
import { serviceStatus } from './ServiceList';
import { InlineRow } from '../ui/InlineRow';
import { cn } from '../../lib/utils';
import { toast } from '../../lib/toast';
import { getTaskJobs, recordActivity } from '../../lib/api';
import { jobViewUrl } from '../../lib/jobView';
import { errorMessage } from '../../lib/errorMessage';
import { clusterStore, useClusterStore } from '../../stores/clusterStore';
import type { ServiceInfo, TaskInfo, TaskJob } from '../../lib/types';

export interface ServicePanelProps {
  item: ServiceInfo;
  tasks?: TaskInfo[];
  accent?: string;
  onScale: (replicas: number, from: number) => Promise<unknown>;
  onClose?: () => void;
}

function taskOk(status: string): boolean {
  return status === 'OK' || status === 'ready';
}

const TASK_ROWS = 24;
const TASK_JOBS_REFRESH_MS = 10_000;

function formatRunning(s: number | null): string {
  if (s === null) return 'início desconhecido';
  if (s < 60) return `rodando há ${s}s`;
  if (s < 3600) return `rodando há ${Math.floor(s / 60)}min`;
  return `rodando há ${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}`;
}

function TaskJobCell({
  taskID,
  jobs,
  loading,
}: {
  taskID: string;
  jobs: TaskJob[] | undefined;
  loading: boolean;
}): ReactElement {
  const [job, ...rest] = jobs ?? [];
  return (
    <span
      data-testid={`task-job-${taskID}`}
      className="whitespace-nowrap font-mono text-[9.5px] text-textMute"
    >
      {job ? (
        <>
          <span
            className="text-text"
            title={`${job.type} · ${job.instance} · ${formatRunning(job.runningForS)}`}
          >
            {job.jobId}
          </span>
          {rest.length > 0 && <span className="ml-1">{`+${rest.length}`}</span>}
        </>
      ) : loading ? (
        '…'
      ) : (
        '—'
      )}
    </span>
  );
}

const STATUS_PILL = {
  running: { label: 'RODANDO', tone: 'ok' },
  zerado: { label: 'ZERADO', tone: 'neutral' },
  degradado: { label: 'DEGRADADO', tone: 'danger' },
} as const;

function PanelSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex-shrink-0">
      <div className="flex items-baseline justify-between px-4 pt-2.5 pb-1">
        <div className="font-mono text-[9.5px] font-bold tracking-[.08em] uppercase text-textMute">
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function ServicePanel({ item, tasks = [], onScale, onClose }: ServicePanelProps) {
  const serviceTasks = tasks.filter(t => t.serviceID === item.id);
  const taskIds = serviceTasks.slice(0, TASK_ROWS).map(t => t.taskID);
  // A job muda a cada poucos segundos: abrir o painel sempre pergunta de novo
  // e o intervalo mantém a coluna viva enquanto ele fica aberto.
  const jobsQ = useQuery({
    queryKey: ['task-jobs', taskIds],
    queryFn: () => getTaskJobs(taskIds),
    enabled: taskIds.length > 0,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: TASK_JOBS_REFRESH_MS,
  });
  const jobsNotice = jobsQ.isError
    ? `Job indisponível: ${errorMessage(jobsQ.error)}`
    : jobsQ.data && jobsQ.data.errors.length > 0
      ? `Job indisponível em ${jobsQ.data.errors
          .map(e => `${e.instance} (${e.error})`)
          .join(', ')}`
      : null;
  const activeSlug = useClusterStore(s => s.activeSlug);
  const status = serviceStatus(item);
  const pill = STATUS_PILL[status];
  // Em "Todos" não existe cluster a quem perguntar: o diagnóstico é por agent.
  const canDiagnose = status === 'degradado' && activeSlug !== 'all';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.replicas);

  useEffect(() => {
    setEditing(false);
    setDraft(item.replicas);
  }, [item.id, item.replicas]);

  const startScale = (): void => {
    setDraft(item.replicas);
    setEditing(true);
  };

  const confirmScale = (): void => {
    setEditing(false);
    if (draft === item.replicas) return;
    onScale(draft, item.replicas).catch((err: unknown) => {
      toast(`Falha ao escalar: ${errorMessage(err)}`, { tone: 'danger' });
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="service-panel">
      <header
        className="flex-shrink-0 border-b border-borderSoft px-4 pt-3.5 pb-3"
        data-testid="service-panel-header"
      >
        <div className="flex items-center gap-2">
          <h2 className="m-0 flex-1 break-all font-mono text-sm font-bold tracking-tight text-text">
            {item.name}
          </h2>
          {onClose && (
            <button
              type="button"
              data-testid="service-panel-close"
              aria-label="Fechar painel do serviço"
              onClick={onClose}
              className="flex-shrink-0 rounded-md p-1 text-textMute hover:text-text"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <StatPill tone={pill.tone}>{pill.label}</StatPill>
          <span className="font-mono text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-surfaceAlt text-textMute">
            {item.type || '—'}
          </span>
          <span className="font-mono text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-accentSoft text-[var(--fm-accent)]">
            {item.system || '—'}
          </span>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-auto">
        {canDiagnose && (
          <ServiceDiagnosis slug={activeSlug} serviceId={item.id} />
        )}
        <PanelSection title="Configuração">
          <div className="px-4 pt-2.5 pb-3.5">
            <InlineRow label="Fila">
              <span className="font-semibold text-text">
                {item.stack || '—'}
              </span>
            </InlineRow>
            <InlineRow label="Sistema">
              <span className="font-mono text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-accentSoft text-[var(--fm-accent)]">
                {item.system || '—'}
              </span>
            </InlineRow>
            <InlineRow label="Tipo">
              <span className="font-mono text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-surfaceAlt text-textMute">
                {item.type || '—'}
              </span>
            </InlineRow>
            <InlineRow label="Réplicas">
              {editing ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    max={50}
                    value={draft}
                    autoFocus
                    onChange={e =>
                      setDraft(Math.max(0, Math.floor(Number(e.target.value) || 0)))
                    }
                    onKeyDown={e => {
                      if (e.key === 'Enter') confirmScale();
                      if (e.key === 'Escape') setEditing(false);
                    }}
                    className="h-6 w-14 rounded border border-border bg-surface px-1.5 text-right font-mono text-[11px] text-text"
                  />
                  <button
                    type="button"
                    aria-label="Confirmar"
                    onClick={confirmScale}
                    className="flex h-6 w-6 items-center justify-center rounded text-ok hover:bg-surfaceAlt"
                  >
                    <Check size={13} />
                  </button>
                  <button
                    type="button"
                    aria-label="Cancelar"
                    onClick={() => setEditing(false)}
                    className="flex h-6 w-6 items-center justify-center rounded text-textMute hover:bg-surfaceAlt"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-text">
                    {item.machines} / {item.replicas}
                  </span>
                  <button
                    type="button"
                    onClick={startScale}
                    className="inline-flex h-6 items-center rounded border border-border bg-surface px-2 text-[10.5px] font-medium text-text hover:bg-surfaceAlt"
                  >
                    Escalar
                  </button>
                </div>
              )}
            </InlineRow>
          </div>
        </PanelSection>

        <PanelSection title={`Réplicas rodando · ${serviceTasks.length}`}>
          <div>
            {serviceTasks.length === 0 && (
              <div className="px-4 py-4 text-[11px] text-textMute">
                Sem réplicas em execução.
              </div>
            )}
            {serviceTasks.length > 0 && (
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 px-4 py-1 text-[9px] font-semibold uppercase tracking-wide text-textMute">
                <span>host</span>
                <span>ip</span>
                <span>job</span>
                <span className="text-right">status</span>
              </div>
            )}
            {serviceTasks.slice(0, TASK_ROWS).map(t => (
              <div
                key={t.taskID}
                className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 border-t border-borderSoft px-4 py-1.5 text-[10.5px]"
              >
                <span
                  className="truncate font-mono text-text"
                  title={`${t.serviceName}.${t.slot}`}
                >
                  {t.hostname || t.nodeID}
                </span>
                <span className="font-mono text-textMute">
                  {t.ip ? (
                    <a
                      className="text-accent hover:underline"
                      href={jobViewUrl(t.ip, t.managerPort)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        recordActivity({
                          action: 'open-ip',
                          cluster: clusterStore.getState().activeSlug,
                          target: t.ip,
                          payload: { service: t.serviceName, host: t.hostname },
                        })
                      }
                    >
                      {t.ip}:{t.managerPort || 32500}
                    </a>
                  ) : (
                    '—'
                  )}
                </span>
                <TaskJobCell
                  taskID={t.taskID}
                  jobs={jobsQ.data?.jobs[t.taskID]}
                  loading={jobsQ.isPending}
                />
                <span className="flex items-center justify-end gap-1 font-mono text-[9.5px] text-textMute">
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      taskOk(t.nodeStatus) ? 'bg-okDot' : 'bg-dangerDot'
                    )}
                  />
                  {taskOk(t.nodeStatus) ? 'ok' : 'down'}
                </span>
              </div>
            ))}
            {jobsNotice && (
              <div className="border-t border-borderSoft px-4 py-1.5 text-[10px] text-textMute">
                {jobsNotice}
              </div>
            )}
          </div>
        </PanelSection>
      </div>
    </div>
  );
}

ServicePanel.displayName = 'ServicePanel';

export default ServicePanel;
